import * as _ from 'lodash'
import {PAdbContext} from "../PA.Core/PAdbContext";
import {PASearch} from "../PA.Core/PASearch";
import {
    WB_BaselineRow,
    WB_CustomerRow,
    WB_PeriodRow,
    WB_PlanRow,
    WB_PlanTypeRow,
    WB_ProductGroupRow,
    WB_TPM_EventRow
} from "../PA.Core/PAdbSchemaRows";
import {
    esWB_BaseLn,
    esWB_Baseline,
    esWB_Customer,
    esWB_Period,
    esWB_Plan,
    esWB_Plan_Type,
    esWB_Product_Group,
    esWB_TPM_Event
} from "../PA.Core/PAdbSchema";
import {PlanBO} from "../PA.TPM.BusinessObjects/PlanBO";
import {AuditService} from "../PA.Core/Security/AuditService";
import {AuditLogType} from "../PA.Core/PA.Core.IF.Audit";

interface ICreateMissingPlanOptions {
    WB_CUST_ID_PLANNING: number,
    WB_PERIOD_ID?: number,
    WB_PLNMGMT_ID?: number,
    bCreatePlanPGRecords?: boolean
}

interface ICreatePlanOptions {
    WB_CUST_ID_PLANNING: number,
    WB_PRDGRP_ID_PLANNING_LEVEL: number,
    WB_PERIOD_ID: number,
    WB_PLANM_ID: number,
    WB_PLNMGMT_ID: number,
    PLAN_NAME?: string
}


export class PlanService {

    private static _dbContext: PAdbContext;

    static createMissingPlans(dbContext: PAdbContext, opts: ICreateMissingPlanOptions): Promise<string[]> {

        //find the missing plans using view to identify WB_ID natural keys
        //create a set of WB_Plans, then save them all at once to the DB
        return new Promise<string[]>((resolve, error) => {

            let whereFieldSpecJSON: any = {
                WB_CUST_ID_PLANNING: {type: "number"},
                WB_PERIOD_ID: {type: "number"},
                WB_PLNMGMT_ID: {type: "number"}
            };

            let whereConditionJSON: any = {WB_CUST_ID_PLANNING: opts.WB_CUST_ID_PLANNING};

            if (_.isNumber(opts.WB_PERIOD_ID)) {
                whereConditionJSON.WB_PERIOD_ID = opts.WB_PERIOD_ID;
            }
            if (_.isNumber(opts.WB_PLNMGMT_ID)) {
                whereConditionJSON.WB_PLNMGMT_ID = opts.WB_PLNMGMT_ID;
            }

            let bCreatePlanPGRecords: boolean = false;
            if (_.isBoolean(opts.bCreatePlanPGRecords)){
                bCreatePlanPGRecords = opts.bCreatePlanPGRecords;
            }

            PASearch.find("vWB_PLNMGMTR_MissingPlans",
                whereConditionJSON,
                whereFieldSpecJSON,
                [],
                0,
                [])
                .then((results: any[]) => {

                    let arrCreatedPlans: string[] = []
                    async function createEachPlan() {
                        let auditService = new AuditService(dbContext);
                        auditService.setReference("PlanManagement", opts.WB_PLNMGMT_ID);
                        for (let idx = 0; idx < results.length; idx++) {
                            let aRow = results[idx];
                            let createdPlan = await PlanService.createPlan(dbContext,
                                {
                                    WB_CUST_ID_PLANNING: aRow.WB_CUST_ID_PLANNING,
                                    WB_PRDGRP_ID_PLANNING_LEVEL: aRow.WB_PRDGRP_ID,
                                    WB_PERIOD_ID: aRow.WB_PERIOD_ID,
                                    WB_PLNMGMT_ID: aRow.WB_PLNMGMT_ID,
                                    WB_PLANM_ID: aRow.WB_PLANM_ID
                                });
                            console.log("Created Plan: ", createdPlan.cif.PLAN_ID, " - ", createdPlan.cif.PLAN_NAME);
                            await auditService.LogSync(AuditLogType.CreateNew, `Plan created (${createdPlan.cif.WB_PLAN_ID}), (${createdPlan.cif.PLAN_NAME}) `);
                            arrCreatedPlans.push(createdPlan.cif.PLAN_ID)
                            await auditService.Save(false);
                            if (bCreatePlanPGRecords) {
                                await PlanService.createPlanPGs(dbContext, createdPlan);
                                console.log("Created Plan PG Records for Plan: ", createdPlan.cif.PLAN_ID, " - ", createdPlan.cif.PLAN_NAME);
                            }
                        }

                    }

                    createEachPlan()
                        .then((ignore) => {
                            resolve(arrCreatedPlans)
                        })
                        .catch((err) => {
                            error(err)
                        })
                })
                .catch((err) => {
                    error(err);
                })
        })

    }

    static async createPlan(dbContext: PAdbContext, opts: ICreatePlanOptions): Promise<WB_PlanRow> {

        let aNewPlan: WB_PlanRow = <any>await dbContext.createRecordWithKey(esWB_Plan)
        aNewPlan.setDefaults();
        aNewPlan.cif.ACTIVE_IND = "1";
        aNewPlan.cif.WB_CUST_ID_PLANNING = opts.WB_CUST_ID_PLANNING
        aNewPlan.cif.WB_CUST_ID_SUBPLANNING = 0
        aNewPlan.cif.WB_PRDGRP_ID_PLANLEVEL = opts.WB_PRDGRP_ID_PLANNING_LEVEL
        aNewPlan.cif.WB_PERIOD_ID = opts.WB_PERIOD_ID
        aNewPlan.cif.WB_PLANM_ID = opts.WB_PLANM_ID
        aNewPlan.cif.WB_PLNMGMT_ID = opts.WB_PLNMGMT_ID

        let aPeriod: WB_PeriodRow = await dbContext.find(esWB_Period, opts.WB_PERIOD_ID)
        aNewPlan.cif.INSTORE_START_DT = aPeriod.cif.PERIOD_START_DT;
        aNewPlan.cif.INSTORE_END_DT = aPeriod.cif.PERIOD_END_DT;
        aNewPlan.cif.SHIPMENT_START_DT = aPeriod.cif.PERIOD_START_DT;
        aNewPlan.cif.SHIPMENT_END_DT = aPeriod.cif.PERIOD_END_DT;

        let aPrdGrp: WB_ProductGroupRow = await dbContext.find(esWB_Product_Group, opts.WB_PRDGRP_ID_PLANNING_LEVEL)
        aNewPlan.cif.PLANNING_LEVEL_ID = aPrdGrp.cif.PRODUCT_GROUP_ID;
        aNewPlan.cif.PLANNING_LEVEL_NAME = aPrdGrp.cif.PRODUCT_GROUP_NAME;

        let aCust: WB_CustomerRow = await dbContext.find(esWB_Customer, opts.WB_CUST_ID_PLANNING)

        aNewPlan.cif.PLAN_NAME = aCust.cif.CUSTOMER_NAME + " " + aNewPlan.cif.PLANNING_LEVEL_NAME + " " + aPeriod.cif.PERIOD_ID;

        let aPlanType: WB_PlanTypeRow = await dbContext.find(esWB_Plan_Type, opts.WB_PLANM_ID)
        aNewPlan.cif.QTY_UOM = aPlanType.cif.QUANTITY_UOM
        aNewPlan.cif.CURRENCY = aPlanType.cif.CURRENCY

        // Copy default HR options
        aNewPlan.BVPPerspectiveOptions = aPlanType.BVPPerspectiveOptions
        aNewPlan.BVPTableOptions = aPlanType.BVPTableOptions
        aNewPlan.SVFPerspectiveOptions = aPlanType.SVFPerspectiveOptions
        aNewPlan.SVFTableOptions = aPlanType.SVFTableOptions
        aNewPlan.MetricPerspectiveOptions = aPlanType.MetricPerspectiveOptions
        aNewPlan.MetricTableOptions = aPlanType.MetricTableOptions

        aNewPlan.cif.PLAN_ROW_ID = _.toString(aNewPlan.cif.WB_PLAN_ID)

        const defaultBaselines: WB_BaselineRow[] = await dbContext.findAll(esWB_Baseline, {
            where: {
                WB_PERIOD_ID:   opts.WB_PERIOD_ID,
                WB_PRDH_ID:     aPlanType.cif.WB_PRDH_ID,
                WB_CUSTH_ID:    aPlanType.cif.WB_CUSTH_ID,
                DELETED_IND:    { neq: '1' }
            }
        })

        if (defaultBaselines.length) {
            const aBaseline: WB_BaselineRow = defaultBaselines[0]
            const aBaselineId: number = aBaseline.cif.WB_BASELINE_ID
            aNewPlan.cif.WB_BASELINE_ID_CNS_PY = aBaselineId
            aNewPlan.cif.WB_BASELINE_ID_CNS_ALT = aBaselineId
            aNewPlan.cif.WB_BASELINE_ID_SHIP_PY = aBaselineId
            aNewPlan.cif.WB_BASELINE_ID_SHIP_ALT = aBaselineId
        }

        let arrBaselinePYSrc = aPlanType.cif.BASELINEPY_SOURCES.split(',')
        let arrBaselineAltSrc = aPlanType.cif.BASELINEALT_SOURCES.split(',')
        let defaultALT = aPlanType.cif.BASELINEALT_SOURCE_DEFAULT
        let defaultPY = aPlanType.cif.BASELINEPY_SOURCE_DEFAULT

        let wherePY: string [] , whereALT: string []
        wherePY = defaultPY ? [defaultPY] : arrBaselinePYSrc
        whereALT = defaultALT ? [defaultALT] : arrBaselineAltSrc
        await dbContext.findAll(esWB_BaseLn, {
            where: {
                SOURCE_ID: {in: wherePY},
                WB_PERIOD_ID: opts.WB_PERIOD_ID,
                WB_CUST_ID: aCust.cif.WB_CUST_ID
            }
        })
            .then((result) => {
                if (result.length) {
                    aNewPlan.cif.WB_BASELINE_ID_PY = result[0].cif.WB_BASELINE_ID
                }

            })
        await dbContext.findAll(esWB_BaseLn, {
            where: {
                SOURCE_ID: {in: whereALT},
                WB_PERIOD_ID: opts.WB_PERIOD_ID,
                WB_CUST_ID: aCust.cif.WB_CUST_ID
            }
        })
            .then((result) => {
                if (result.length) {
                    aNewPlan.cif.WB_BASELINE_ID_ALT = result[0].cif.WB_BASELINE_ID
                }

            })
        aNewPlan.cif.SOURCE_ID = "WB"
        await dbContext.Save(esWB_Plan, aNewPlan)

        return aNewPlan

    }

    static async createPlanPGs(dbContext: PAdbContext, planRow: WB_PlanRow) : Promise<void> {

        let plnBo: PlanBO = new PlanBO({dbContext: dbContext});
        let loadedBo = await plnBo.Load(planRow.cif.WB_PLAN_ID, {dbContext: dbContext});
        if (!loadedBo.bIsLoaded) {
            console.log("Unable to load BO for Plan: ", planRow.cif.PLAN_ID, " - ", planRow.cif.PLAN_NAME);
            return;
        }

        await plnBo.OnIHaveProductGroupsLoaded;
        if (!plnBo._bIHaveProductGroupsLoaded){
            console.log("Unable to load Product Groups for Plan: ", planRow.cif.PLAN_ID, " - ", planRow.cif.PLAN_NAME);
            return;
        }

        for (let aPrdGrpRow of plnBo.ProductGroups){
            await plnBo.addProductGroup(aPrdGrpRow.cif.WB_PRDGRP_ID);
        }

        await plnBo.Save();
    }

    static getPlanByWbID(planWbID: number): Promise<WB_PlanRow> {
        return new Promise<WB_PlanRow>((resolve, error) => {
            if (_.isUndefined(PlanService._dbContext)) {
                PlanService._dbContext = new PAdbContext();
            }

            PlanService._dbContext.find(esWB_Plan, planWbID)
                .then((result) => {
                    resolve(result);
                })
                .catch((err) => {
                    error(err);
                })
        })
    }

    static getTPMEventRecordByWBTEvtID(tEvtWbID: number): Promise<WB_TPM_EventRow> {
        return new Promise<WB_TPM_EventRow>((resolve, error) => {

            if (_.isUndefined(PlanService._dbContext)) {
                PlanService._dbContext = new PAdbContext();
            }

            PlanService._dbContext.find(esWB_TPM_Event, tEvtWbID)
                .then((result) => {
                    resolve(result);
                })
                .catch((err) => {
                    error(err);
                })
        })
    }

    static getTPMEventRecordsByPlanWbID(planWbID: number): Promise<WB_TPM_EventRow[]> {

        return new Promise<WB_TPM_EventRow[]>((resolve, error) => {
            if (_.isUndefined(PlanService._dbContext)) {
                PlanService._dbContext = new PAdbContext();
            }

            let query: any = {where: {DELETED_IND: {neq: "1"}, WB_PLAN_ID: planWbID}};
            PlanService._dbContext.findAll(esWB_TPM_Event, query)
                .then((results) => {
                    if (_.isArray(results)) {
                        resolve(results);
                    }
                })
                .catch((err) => {
                    error(err);
                })
        })
    }

}
